import { UPLOAD_LRS_SUCCESS } from 'constants/index';
import merge from 'lodash/merge';
import pull from 'lodash/pull';
import without from 'lodash/without';
import type { Field, Payment } from 'models';
import {
  CANCEL_PAYMENT_SUCCESS,
  CREATE_PAYMENT_SUCCESS,
  DELETE_PAYMENT_LRS_FAILURE,
  DELETE_PAYMENT_LRS_SUCCESS,
  FETCH_PAYMENT_LRS_FAILURE,
  FETCH_PAYMENT_LRS_SUCCESS,
  FETCH_PAYMENT_SUCCESS,
  FETCH_TRACKING_PAGE_SUCCESS,
  INITIATE_PAYMENT_SUCCESS,
  RESTART,
  SELECT_PAYMENT,
  UPDATE_SENDER_EMAIL,
} from '../../../constants';

type PaymentAction =
  | {
      type:
        | typeof SELECT_PAYMENT
        | typeof CREATE_PAYMENT_SUCCESS
        | typeof FETCH_PAYMENT_SUCCESS;
      payload: {
        entities: {
          payments: Record<string, Payment>;
        };
        result: string;
      };
    }
  | {
      type: typeof CANCEL_PAYMENT_SUCCESS;
      payload: {
        id: string;
        cancelledAt: string;
      };
    }
  | {
      type: typeof FETCH_TRACKING_PAGE_SUCCESS;
      payload: {
        recurring?: boolean;
      };
    }
  | {
      type: typeof INITIATE_PAYMENT_SUCCESS;
      payload: {
        id: string;
        paymentInitiatedAt: string;
      };
    }
  | {
      type: typeof UPDATE_SENDER_EMAIL;
      payload: string;
    }
  | { type: typeof FETCH_PAYMENT_LRS_FAILURE }
  | { type: typeof FETCH_PAYMENT_LRS_SUCCESS }
  | { type: typeof DELETE_PAYMENT_LRS_FAILURE }
  | { type: typeof DELETE_PAYMENT_LRS_SUCCESS }
  | { type: typeof UPLOAD_LRS_SUCCESS }
  | { type: typeof RESTART };

const payment = (
  state: Payment | Record<string, never> = {},
  action: PaymentAction,
) => {
  switch (action.type) {
    case CANCEL_PAYMENT_SUCCESS:
      const { cancelledAt } = action.payload;

      const links = without(
        state.links,
        'initiate',
        'cancellation',
        'sms_notification',
      );

      return {
        ...state,
        instructions: [],
        links,
        status: 'cancelled',
        statusTransitions: { cancelledAt },
      };
    case CREATE_PAYMENT_SUCCESS:
    case FETCH_PAYMENT_SUCCESS:
    case SELECT_PAYMENT:
      const {
        entities: { payments },
        result,
      } = action.payload;
      return payments[result];
    case INITIATE_PAYMENT_SUCCESS:
      const senderIndicatedSentAt = action.payload.paymentInitiatedAt;
      const newInitiatedState = merge({}, state, { senderIndicatedSentAt });
      pull(newInitiatedState.links, 'initiate', 'cancellation');

      return newInitiatedState;
    case RESTART:
      return {};
    case FETCH_TRACKING_PAGE_SUCCESS:
      const { recurring } = action.payload;
      return { ...state, recurring };
    case FETCH_PAYMENT_LRS_SUCCESS:
    case UPLOAD_LRS_SUCCESS:
      return { ...state, isPaymentLRSSubmitted: true };
    case FETCH_PAYMENT_LRS_FAILURE:
    case DELETE_PAYMENT_LRS_SUCCESS:
    case DELETE_PAYMENT_LRS_FAILURE:
      return { ...state, isPaymentLRSSubmitted: false };
    case UPDATE_SENDER_EMAIL:
      const { sender } = state;
      const value = action.payload;
      const isEmail = ({ id }: { id: string }) => id === 'email';
      const updateEmail = (field: Field) =>
        isEmail(field) ? { ...field, value } : field;

      const fields = sender.fields.map(updateEmail);

      return { ...state, sender: { ...sender, fields } };
    default:
      return state;
  }
};

export { payment };
